import React, {
  FC,
  useMemo,
  useRef,
  useEffect,
  useState,
  MutableRefObject
} from 'react';
import cx from 'classnames';
import { Button, Tag, Popconfirm } from 'antd';
import { getMentionableUsers } from './AtMention/getMentionableUsers';
import PrivateToggle from './PrivateToggle';
import { canUserViewPrivateComments } from '../permissions';
import * as translations from './strings';
import * as atMentionTranslations from './AtMention/strings';
import { getLangKey } from '../../../../../../models/Language';
import { User } from '../../../../../../models/User';
import type { MemberRole } from '../../../../../../models/CurrentUserProject';
import { RichTextComment, COMMENT_COMPOSER_COLLAPSE_MS } from '../../../../../../components/RichTextComment';
import type {
  MarkdownMentionPreviewContext,
  User as RichTextUser
} from '../../../../../../components/RichTextDescription/types';
import styles from './styles.module.css';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import {
  CommentsState,
  useCommentsState
} from '../../../../../../../providers/Comments';
import {
  emitAttachmentPasteFiles,
  getClipboardFiles
} from '../../../../../../../../redesign/javascript/utils/clipboardFiles';

export const MAX_COMMENT_LENGTH: number = 2000;

const strings = translations[getLangKey()];
const atMentionStrings = atMentionTranslations[getLangKey()];

type Props = {
  initialValue?: string;
  commentId?: number;
  onCancel?: () => void;
  isPrivate?: boolean;
  uploadedAttachment?: {
    name: string;
    url: string;
    uploadedAt: number;
  } | null;
  showUpgrade?: boolean;
  setShowUpgrade?: (showUpgrade: boolean) => void;
  handlePrivateToggle?: (checked: boolean) => void;
};

const sanitizeAttachmentName = (name: string): string =>
  name.replace(/\\/g, '\\\\').replace(/\]/g, '\\]');

const IMAGE_ATTACHMENT_REGEX =
  /\.(?:png|jpe?g|gif|webp|bmp|svg|avif)$/i;

const getAttachmentBaseName = (url: string): string =>
  (url.split('#')[0].split('?')[0].split('/').pop() || '').trim();

const isImageAttachment = (attachment: { name?: string | null; url: string }): boolean => {
  const candidateName = attachment.name?.trim() || getAttachmentBaseName(attachment.url);
  return IMAGE_ATTACHMENT_REGEX.test(candidateName);
};

export const appendAttachmentLinkToComment = (
  existingComment: string,
  attachment: { name?: string | null; url: string }
): string => {
  const { url } = attachment;
  if (!url) return existingComment;

  const displayName = sanitizeAttachmentName(
    attachment.name?.trim() || getAttachmentBaseName(url) || 'attachment'
  );
  const separator =
    existingComment.length > 0 && !existingComment.endsWith('\n') ? '\n' : '';
  const prefix = isImageAttachment(attachment) ? '!' : '';

  return `${existingComment}${separator}${prefix}[${displayName}](${url})`;
};

const AddComment: FC<Props> = ({
  initialValue,
  commentId,
  onCancel,
  isPrivate,
  uploadedAttachment,
  showUpgrade,
  setShowUpgrade,
  handlePrivateToggle
}) => {
  const {
    getContainer,
    currentUserId,
    users,
    createComment,
    updateComment,
    canViewPrivateComments,
    canCreatePrivateComments,
    billingRights,
    owner,
    bugherdUrl,
    organizationId,
    isSmallView,
    isAdminView,
    onPlanUpgradeClick,
    taskId,
    guestVisible,
    assignGuestsAllowed,
    assignGuests,
    assignees,
    usersNotOnProject,
    mentionableTasks,
    setActiveTask
  } = useCommentsState() as CommentsState;
  const submitKey = () =>
    navigator.platform.indexOf('Mac') > -1 ? 'control' : 'ctrl';
  const _initialValue = initialValue || '';
  const emptyFunc = () => {};
  const _onCancel: () => void = onCancel || emptyFunc;

  const [hasFocus, setHasFocus] = useState<boolean>(false);
  const [comment, setComment] = useState<string>(_initialValue);
  const [wrongShortcutWarning, setWrongShortcutWarning] = useState<boolean>(
    false
  );

  const mentionRef: MutableRefObject<HTMLInputElement | null> = useRef(null);
  const composerContainerRef = useRef<HTMLDivElement>(null);
  const composerWrapRef = useRef<HTMLDivElement>(null);
  const interactingWithActionsRef = useRef(false);
  const clearComposerTimeoutRef = useRef<number | null>(null);
  const lastAttachmentKeyRef = useRef<string | null>(null);
  const [isCollapsing, setIsCollapsing] = useState(false);
  const editMode: boolean = !!_initialValue;
  const hidePrivateCommentsLabel = isAdminView && isSmallView;
  const uiSize: SizeType = 'middle';
  const hasChanges: boolean = comment !== _initialValue;
  const isNewComment: boolean = !_initialValue;

  useEffect(() => {
    if (_initialValue) {
      mentionRef?.current?.focus();
    }
  });

  useEffect(
    () => () => {
      if (clearComposerTimeoutRef.current != null) {
        window.clearTimeout(clearComposerTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    const composer = composerWrapRef.current;
    if (!composer) return undefined;

    const handlePaste = (event: ClipboardEvent) => {
      const files = getClipboardFiles(event.clipboardData);
      if (!emitAttachmentPasteFiles(files)) return;

      event.preventDefault();
      event.stopPropagation();
      setHasFocus(true);
    };

    composer.addEventListener('paste', handlePaste, true);
    return () => composer.removeEventListener('paste', handlePaste, true);
  }, []);

  useEffect(() => {
    if (!uploadedAttachment?.url) return;
    const attachmentKey = `${uploadedAttachment.url}:${uploadedAttachment.uploadedAt}`;
    if (attachmentKey === lastAttachmentKeyRef.current) return;

    setComment(currentComment =>
      appendAttachmentLinkToComment(currentComment, uploadedAttachment)
    );
    lastAttachmentKeyRef.current = attachmentKey;
  }, [uploadedAttachment]);

  const collapseComposer = () => {
    const editable = composerWrapRef.current?.querySelector(
      '[contenteditable="true"], textarea'
    ) as HTMLElement | null;
    editable?.blur();
    setHasFocus(false);
    mentionRef?.current?.blur();
    setWrongShortcutWarning(false);
    _onCancel();
  };

  const handleBlur = () => {
    collapseComposer();
  };

  const handleComposerBlur = (
    event: React.FocusEvent<HTMLDivElement>
  ) => {
    if (interactingWithActionsRef.current) return;

    const nextFocusedElement = event.relatedTarget as Node | null;
    if (
      nextFocusedElement &&
      composerContainerRef.current?.contains(nextFocusedElement)
    ) {
      return;
    }

    setHasFocus(false);
  };

  const handleActionsMouseDown = (
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    interactingWithActionsRef.current = true;
    event.preventDefault();
    window.setTimeout(() => {
      interactingWithActionsRef.current = false;
    }, 0);
  };

  const resetInput = () => setComment(_initialValue);

  const handleSubmitComment = (_comment: string) => {
    if (!_comment) return;

    const commentBody = {
      text: _comment.trim(),
      isPrivate
    };

    if (commentId) {
      updateComment({
        commentId,
        commentEvent: {
          type: 'edit',
          text: commentBody.text
        },
        taskId
      });
    } else {
      createComment(commentBody);
    }

    setIsCollapsing(true);
    setHasFocus(false);
    setWrongShortcutWarning(false);
    _onCancel();
    if (clearComposerTimeoutRef.current != null) {
      window.clearTimeout(clearComposerTimeoutRef.current);
    }
    clearComposerTimeoutRef.current = window.setTimeout(() => {
      resetInput();
      setIsCollapsing(false);
      clearComposerTimeoutRef.current = null;
    }, COMMENT_COMPOSER_COLLAPSE_MS);
  };

  const handleSubmitShortcut = () => {
    setComment(comment.trim());
    handleSubmitComment(comment.trim());
  };

  const richTextMentionableUsers: RichTextUser[] = useMemo(
    () =>
      getMentionableUsers(users, {
        isPrivate: !!isPrivate,
        guestVisible,
        assignGuestsAllowed,
        assignGuests,
        assignees,
        excludeUserId: currentUserId
      }).map(user => ({
        id: user.id,
        name: user.name,
        email: user.email ?? '',
        avatar: user.avatar,
        role: user.role as unknown as MemberRole,
        firstName: user.firstName ?? user.first_name,
        surname: user.surname
      })),
    [users, isPrivate, guestVisible, assignGuestsAllowed, assignGuests, assignees, currentUserId]
  );

  const mentionPreviewContext: MarkdownMentionPreviewContext = useMemo(
    () => ({
      descriptionMarkdown: comment,
      projectMembers: users,
      allUsers: [...users, ...usersNotOnProject],
      mentionableTasks,
      setActiveTask,
      isComment: true,
      isPrivate: !!isPrivate
    }),
    [comment, users, usersNotOnProject, mentionableTasks, setActiveTask, isPrivate]
  );

  const hasInvalidMentions = () => {
    if (!comment) return false;

    const mentions: string[] = comment.match(
      /\[([^\[]+)\]\(([\d\)]+)\)/g
    ) as string[];

    if (!mentions) return false;

    const ids: number[] = mentions.map(mention =>
      // @ts-ignore
      parseInt(mention.match(/\(([\d]+)+\)/)[1], 10)
    );

    const mentionedUsers: User[] = users.filter((user: User) =>
      ids.includes(
        typeof user.id === 'string' ? parseInt(user.id, 10) : user.id
      )
    );
    return !!mentionedUsers.find(
      (user: User) => !canUserViewPrivateComments(user)
    );
  };

  return (
    <div
      ref={composerContainerRef}
      className={cx(styles.container, {
        [styles.active]: hasFocus,
        [styles.shortcutShown]: wrongShortcutWarning
      })}
    >
      <div
        ref={composerWrapRef}
        className={styles.mentionWrapper}
        onFocus={() => setHasFocus(true)}
        onBlur={handleComposerBlur}
      >
        <RichTextComment
            value={comment}
            onChange={setComment}
            mentionableUsers={richTextMentionableUsers}
            placeholder={atMentionStrings.addComment}
            submitTask={handleSubmitShortcut}
            inputDataCy="rich-text-comment"
            autoFocus={editMode}
            mentionPreviewContext={mentionPreviewContext}
            isPrivate={!!isPrivate}
            isCollapsing={isCollapsing}
            canTogglePrivateComment={!!canViewPrivateComments && isNewComment}
            canCreatePrivateComments={canCreatePrivateComments}
            isPrivateComment={!!isPrivate}
            hasInvalidMentions={hasInvalidMentions()}
            onPrivateCommentToggle={handlePrivateToggle}
            onPrivateCommentUpgrade={() => setShowUpgrade?.(true)}
          />
      </div>
      <div
        className={styles.actions}
        onMouseDownCapture={handleActionsMouseDown}
      >
        <div className={styles.footer}>
          {wrongShortcutWarning && (
            <div className={styles.submissionShortcut}>
              <Tag>
                {submitKey()} + {strings.enter}
              </Tag>{' '}
              {strings.toSubmit}
            </div>
          )}
        </div>
        <div className={styles.actionsInner}>
          <Button
            type="primary"
            data-cy="add-comment-submit"
            onMouseDown={event => event.preventDefault()}
            onClick={() => handleSubmitComment(comment)}
            className={styles.roundedButtonOverride}
            size={uiSize}
            disabled={!comment.length}
            title={!comment.length ? strings.addSomeContent : undefined}
          >
            {editMode ? strings.save : strings.add}
          </Button>
          {hasChanges ? (
            <Popconfirm
              title={strings.discardChanges}
              // @ts-ignore
              getPopupContainer={getContainer}
              onConfirm={() => {
                resetInput();
                handleBlur();
              }}
            >
              <Button
                type="text"
                className={styles.cancel}
                size={uiSize}
                onMouseDown={event => event.preventDefault()}
              >
                {strings.cancel}
              </Button>
            </Popconfirm>
          ) : (
            <Button
              type="text"
              className={styles.roundedButtonOverride}
              size={uiSize}
              onMouseDown={event => event.preventDefault()}
              onClick={handleBlur}
            >
              {strings.cancel}
            </Button>
          )}
        </div>
        <div className={styles.privateAndDeleteContainer}>
          {canViewPrivateComments &&
            isNewComment &&
            handlePrivateToggle &&
            setShowUpgrade && (
              <PrivateToggle
                isPrivate={!!isPrivate}
                // @ts-ignore
                getContainer={getContainer}
                canCreatePrivateComments={canCreatePrivateComments}
                handleFocus={() => setHasFocus(true)}
                mentionRef={mentionRef}
                uiSize={uiSize}
                handlePrivateToggle={handlePrivateToggle}
                setShowUpgrade={setShowUpgrade}
                showUpgrade={!!showUpgrade}
                hasInvalidMentions={hasInvalidMentions()}
                currentUserId={currentUserId}
                organizationId={organizationId}
                billingRights={billingRights}
                ownerEmail={owner ? owner.email : undefined}
                bugherdUrl={bugherdUrl}
                hideLabel={hidePrivateCommentsLabel}
                onPlanUpgradeClick={onPlanUpgradeClick}
              />
            )}
        </div>
      </div>
    </div>
  );
};

export default AddComment;
